import { supabase } from './supabase';

export interface UploadResult {
  success: boolean;
  url?: string;
  path?: string;
  error?: string;
}

export interface ParsedResumeContent {
  text: string;
  filename: string;
  fileType: string;
  extractedData?: {
    name?: string;
    email?: string;
    phone?: string;
    skills?: string[];
    experience?: string[];
    education?: string[];
  };
}

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'application/vnd.ms-word',
];

const MAX_FILE_SIZE = 5 * 1024 * 1024;

class FileUploadService {
  validateFile(file: File): { valid: boolean; error?: string } {
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: 'File size must be under 5MB' };
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type) && !file.name.match(/\.(pdf|doc|docx|txt)$/i)) {
      return { valid: false, error: 'Only PDF, DOC, DOCX, and TXT files are allowed' };
    }

    return { valid: true };
  }

  async uploadResume(userId: string, file: File, category: 'original' | 'generated' | 'uploaded' = 'uploaded'): Promise<UploadResult> {
    try {
      const validation = this.validateFile(file);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${category}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('resumes')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('Supabase storage error:', error);
        return { success: false, error: 'Failed to upload file to storage' };
      }

      const { data: urlData } = supabase.storage
        .from('resumes')
        .getPublicUrl(data.path);

      await supabase.from('resume_attachments').insert({
        user_id: userId,
        file_name: file.name,
        file_path: data.path,
        file_type: file.type,
        file_size: file.size,
        storage_bucket: 'resumes',
      });

      return {
        success: true,
        url: urlData.publicUrl,
        path: data.path,
      };
    } catch (error) {
      console.error('Upload error:', error);
      return { success: false, error: 'An unexpected error occurred during upload' };
    }
  }

  async parseResumeContent(file: File): Promise<ParsedResumeContent> {
    try {
      const text = await this.extractTextFromFile(file);

      const extractedData = this.extractBasicInfo(text);

      return {
        text,
        filename: file.name,
        fileType: file.type,
        extractedData,
      };
    } catch (error) {
      console.error('Parse error:', error);
      return {
        text: '',
        filename: file.name,
        fileType: file.type,
      };
    }
  }

  private async extractTextFromFile(file: File): Promise<string> {
    if (file.type === 'text/plain') {
      return await file.text();
    }

    if (file.type === 'application/pdf') {
      return 'PDF parsing requires additional library. Content extraction pending...';
    }

    if (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) {
      return 'Word document parsing requires additional library. Content extraction pending...';
    }

    return 'Unsupported file type for text extraction';
  }

  private extractBasicInfo(text: string): ParsedResumeContent['extractedData'] {
    const emailRegex = /[\w.-]+@[\w.-]+\.\w+/g;
    const phoneRegex = /(\+\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;

    const emails = text.match(emailRegex);
    const phones = text.match(phoneRegex);

    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    const name = lines.length > 0 ? lines[0] : undefined;

    const skillKeywords = ['skills', 'technical skills', 'core competencies'];
    const expKeywords = ['experience', 'work history', 'employment'];
    const eduKeywords = ['education', 'academic', 'degree'];

    const skills: string[] = [];
    const experience: string[] = [];
    const education: string[] = [];

    let currentSection: 'skills' | 'experience' | 'education' | null = null;

    for (const line of lines) {
      const lowerLine = line.toLowerCase();

      if (skillKeywords.some(kw => lowerLine.includes(kw))) {
        currentSection = 'skills';
        continue;
      }
      if (expKeywords.some(kw => lowerLine.includes(kw))) {
        currentSection = 'experience';
        continue;
      }
      if (eduKeywords.some(kw => lowerLine.includes(kw))) {
        currentSection = 'education';
        continue;
      }

      if (currentSection === 'skills' && line.length < 100) {
        skills.push(line);
      } else if (currentSection === 'experience') {
        experience.push(line);
      } else if (currentSection === 'education') {
        education.push(line);
      }
    }

    return {
      name,
      email: emails?.[0],
      phone: phones?.[0],
      skills: skills.slice(0, 20),
      experience: experience.slice(0, 10),
      education: education.slice(0, 5),
    };
  }

  async deleteResume(filePath: string): Promise<boolean> {
    try {
      const { error } = await supabase.storage
        .from('resumes')
        .remove([filePath]);

      if (error) {
        console.error('Delete error:', error);
        return false;
      }

      await supabase
        .from('resume_attachments')
        .delete()
        .eq('file_path', filePath);

      return true;
    } catch (error) {
      console.error('Delete error:', error);
      return false;
    }
  }

  async getUploadedResumes(userId: string) {
    try {
      const { data, error } = await supabase
        .from('resume_attachments')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching uploaded resumes:', error);
      return [];
    }
  }
}

export const fileUploadService = new FileUploadService();
